// ═══════════════════════════════════════════════════════════════════════════
// Lectura de la Constancia de Situacion Fiscal (PDF del SAT)
// Extraído de index.html v0.9.16. Desde 0.60 lee también su código QR y
// confirma los datos con el SAT (ver csf-sat.js).
// ═══════════════════════════════════════════════════════════════════════════

import jsQR from "jsqr";
import { extraerCamposDeLineas } from "./csf-extractor.js";
import { idDeQrCsf, camposDeValidadorSat, combinarConSat } from "./csf-sat.js";
import { supabase } from "../auth/sesion.js";
import { configSupabase } from "../auth/config.js";

/**
 * El QR de la constancia, dibujando la primera página. Va en la esquina
 * superior izquierda de la cédula; se dibuja la página completa porque sale
 * barato y no depende de que el SAT mueva el recuadro.
 * @returns {{ d3, idCIF, rfc } | null}
 */
async function leerQr(pdf) {
  const page = await pdf.getPage(1);
  for (const escala of [2, 3]) {
    const viewport = page.getViewport({ scale: escala });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    await page.render({ canvasContext: ctx, viewport }).promise;
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const qr = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
    const id = qr && idDeQrCsf(qr.data);
    if (id) return id;
  }
  return null;
}

/** Pide al servidor la página del validador del SAT para ese QR. */
async function consultarSat(d3) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("No hay sesión iniciada.");
  const { url, anonKey } = configSupabase();
  const r = await fetch(`${url.replace(/\/$/, "")}/functions/v1/validar-csf`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${session.access_token}`, "apikey": anonKey,
               "Content-Type": "application/json" },
    body: JSON.stringify({ d3 }),
  });
  const res = await r.json().catch(() => ({}));
  if (!r.ok || !res.ok) throw new Error(res?.error || `El servidor devolvió ${r.status}`);
  return res.html;
}

/**
 * @param opciones.conActividades  hace falta el TEXTO aunque el SAT conteste,
 *   porque se van a leer las actividades económicas (el alta de una vinícola).
 *   Sin esto, una CSF sin texto confirmada por el SAT se salta el OCR, que
 *   tarda medio minuto y ya no aportaría nada.
 */
export async function parseCSF(file, onStatus, { conActividades = false } = {}) {
  if (onStatus) onStatus("Leyendo PDF...");
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = "";
  let usedOcr = false;

  // ── Paso 0: el QR, y con él el SAT ──────────────────────────────────────
  //
  // Primero, porque es lo más confiable: no depende de cómo se parta el
  // texto ni de que el PDF tenga texto. Si algo falla —sin QR, sin red, el
  // SAT caído— se sigue por el camino de siempre y se dice por qué.
  let sat, qr = null;
  try {
    if (onStatus) onStatus("Buscando el código QR...");
    qr = await leerQr(pdf);
    if (qr) {
      if (onStatus) onStatus("Confirmando con el SAT...");
      sat = camposDeValidadorSat(await consultarSat(qr.d3), qr.rfc);
    } else {
      sat = { _sinRespuesta: true, _warnings: [{ level: "info",
        message: "No se encontró el código QR de la constancia: los datos se leyeron del PDF." }] };
    }
  } catch (e) {
    console.warn("[CSF] sin confirmación del SAT:", e?.message);
    sat = { _sinRespuesta: true, _warnings: [{ level: "info",
      message: `No se pudo confirmar con el SAT (${e?.message || "error"}): los datos se leyeron del PDF.` }] };
  }
  const satContesto = !sat._sinRespuesta;

  // Step 1: Try native text extraction
  for (let i = 1; i <= Math.min(pdf.numPages, 2); i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const items = [];

    content.items.forEach(item => {
      if (item.str.trim()) {
        items.push({
          text: item.str.trim(),
          x: Math.round(item.transform[4]),
          y: Math.round(item.transform[5]),
        });
      }
    });

    items.sort((a, b) => b.y - a.y || a.x - b.x);

    const lines = [];
    let currentLine = [];
    let lastY = null;
    items.forEach(item => {
      if (lastY !== null && Math.abs(item.y - lastY) > 4) {
        if (currentLine.length > 0) lines.push(currentLine.map(i => i.text).join(" "));
        currentLine = [];
      }
      currentLine.push(item);
      lastY = item.y;
    });
    if (currentLine.length > 0) lines.push(currentLine.map(i => i.text).join(" "));
    fullText += lines.join("\n") + "\n";
  }

  // Step 2: If text extraction failed, try OCR
  const meaningfulText = fullText.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ0-9]/g, "").length;
  if (meaningfulText < 50 && (!satContesto || conActividades)) {
    if (onStatus) onStatus("PDF sin texto — cargando OCR...");
    try {
      // Load Tesseract.js on demand
      if (!window.Tesseract) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";
          s.onload = resolve;
          s.onerror = () => reject(new Error("No se pudo cargar Tesseract.js"));
          document.head.appendChild(s);
        });
      }

      if (onStatus) onStatus("Procesando OCR (puede tardar 20-30s)...");
      fullText = "";
      for (let i = 1; i <= Math.min(pdf.numPages, 2); i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2.0 }); // Higher scale = better OCR
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;

        if (onStatus) onStatus(`OCR página ${i}/${Math.min(pdf.numPages, 2)}...`);
        const { data } = await window.Tesseract.recognize(canvas, "spa", {
          logger: m => { if (m.status === "recognizing text" && onStatus) onStatus(`OCR página ${i}: ${Math.round((m.progress || 0) * 100)}%`); }
        });
        fullText += data.text + "\n";
      }
      usedOcr = true;
      console.log("[CSF Parser] OCR text:\n", fullText);
    } catch (ocrErr) {
      console.warn("[CSF Parser] OCR failed:", ocrErr);
      fullText = "";
    }
  }

  if (onStatus) onStatus("Extrayendo datos...");

  // ── Extracción de campos ──
  // Desde v0.9.19 la hace el extractor de etiquetas (csf-extractor.js): un
  // valor termina donde empieza la siguiente etiqueta, nunca al fin de línea.
  // Las regex anteriores corrompieron los domicilios de 9 clientes reales
  // (ver commit "Auditoria de domicilios").
  const lines = fullText.split("\n").map(l => l.trim()).filter(Boolean);
  const delPdf = {
    ...extraerCamposDeLineas(lines),
    // Las líneas YA normalizadas —recortadas y sin vacías—, que es lo que vio
    // el extractor. Se exponen para que quien necesite leer otra sección
    // (p. ej. las actividades económicas) parta del mismo texto y no vuelva a
    // partirlo con reglas ligeramente distintas.
    _lineas: lines,
    _rawText: fullText,
    _usedOcr: usedOcr,
    _noText: meaningfulText < 50 && !usedOcr,
  };
  // Un PDF sin texto no deja huella en la comparación con el SAT: sus campos
  // vacíos no son "diferencias".
  const result = combinarConSat(delPdf, sat);
  result._qr = qr ? { idCIF: qr.idCIF, rfc: qr.rfc } : null;
  if (result._noText) {
    result._warnings.push({ level: "critical",
      message: "El PDF no contiene texto legible y el OCR no está disponible." });
  }

  return result;
}
